import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import RadioButton from 'LuxKit/Radio/RadioButton'
import TextLink from 'LuxKit/TextLink'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyText from 'LuxKit/Typography/BodyText'
import List from 'components/Common/List/List'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Expander from 'components/utils/Expander'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useUserTierProgress from 'loyalty/hooks/useUserTierProgress'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LOYALTY_REWARDS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useLoyaltyChooseDoubleRewards from '../hooks/useLoyaltyChooseDoubleRewards'
import type { ChooseDoubleRewardsChoice } from '../utils/loyaltyChooseDoubleRewardsState'
import LoyaltyRewardChoiceGradientHeader from './LoyaltyRewardChoiceGradientHeader'
import useLoyaltyChooseDoubleRewardsTracking from '../hooks/useLoyaltyChooseDoubleRewardsTracking'
import LoyaltyPlatinumRetentionMessage from './LoyaltyPlatinumRetentionMessage'
import useLoyaltyPlatinumRetention from '../hooks/useLoyaltyPlatinumRetention'
import LoyaltyRewardChoiceTierProgress from './LoyaltyRewardChoiceTierProgress'
import { CHOOSE_DOUBLE_REWARDS_SLUG } from '../loyaltyChooseDoubleRewardsTakeover.constants'
import noop from 'lib/function/noop'

const Card = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(12)};
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 0.2s;

  &:has(input:checked) {
    border-width: 2px;
    border-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    padding: ${rem(15)};
  }
`

const SelectionContent = styled(Group)`
  padding-block-start: ${rem(24)};
`

const OptionContent = styled(Group)`
  flex: 1;
  min-width: 0;
`

const DetailList = styled(List)`
  padding-block-start: ${rem(8)};
  padding-inline-start: ${rem(18)};
`

function renderBold(chunks: ReactNode) {
  return <strong>{chunks}</strong>
}

function renderEmphasis(chunks: ReactNode) {
  return <strong><em>{chunks}</em></strong>
}

interface Props {
  initialSelection?: ChooseDoubleRewardsChoice
  onContinue: (choice: ChooseDoubleRewardsChoice) => void
}

function LoyaltySelectRewardChoiceContent({ initialSelection, onContinue }: Props) {
  const { impressionRef, track } = useLoyaltyChooseDoubleRewardsTracking('choice_modal')
  const intl = useIntl()
  const modal = useModalElementContext<ChooseDoubleRewardsChoice | undefined>()
  const [selection, setSelection] = useState<ChooseDoubleRewardsChoice | undefined>(initialSelection)
  const [pointsExpanded, setPointsExpanded] = useState(false)
  const [statusExpanded, setStatusExpanded] = useState(false)

  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const account = useAppSelector(state => state.loyalty.account.data)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const progress = useUserTierProgress()
  const platinumRetention = useLoyaltyPlatinumRetention()
  const { endsAt, memberState, canEnrol } = useLoyaltyChooseDoubleRewards()
  const showStatusCredits = (memberState.status === 'enrolled' && memberState.choice === 'statusCredits') || tier !== 'infinite'

  const availableSelection = !canEnrol || (selection === 'statusCredits' && !showStatusCredits) ? undefined : selection
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const selectPointsOption = useCallback(
    () => {
      if (selection !== 'points') track('option_selected_points')
      setSelection('points')
    },
    [selection, track],
  )
  const selectStatusCreditsOption = useCallback(
    () => {
      if (selection !== 'statusCredits') track('option_selected_statusCredits')
      setSelection('statusCredits')
    },
    [selection, track],
  )

  const togglePoints = useCallback(
    () => {
      if (!pointsExpanded) track('option_expanded_points')
      setPointsExpanded(!pointsExpanded)
    },
    [pointsExpanded, track],
  )
  const toggleStatus = useCallback(
    () => {
      if (!statusExpanded) track('option_expanded_statusCredits')
      setStatusExpanded(!statusExpanded)
    },
    [statusExpanded, track],
  )

  const continueSelection = useCallback(
    () => {
      if (availableSelection) {
        onContinue(availableSelection)
      }
    }, [onContinue, availableSelection])
  const dismiss = useCallback(
    () => modal.resolve(undefined),
    [modal],
  )

  const renderRewardSeatsLink = useCallback((chunks: ReactNode) => (
    <TextLink key="reward-seats" to={`/${LOYALTY_REWARDS_PAGE}`} weight="regular" underline onClick={dismiss}>{chunks}</TextLink>
  ), [dismiss])

  const title = intl.formatMessage({
    id: '6le4QedFgkkzKenjQJNN',
    defaultMessage: 'Choose your <emphasis>double rewards</emphasis>',
    description: 'Title of the modal for choosing between the double reward campaign options',
  }, { emphasis: renderEmphasis })

  const continueLabel = intl.formatMessage({
    id: '6zBEb2sCAkLIBWqrzA98',
    defaultMessage: 'Continue with selection',
    description: 'Button to proceed to confirmation of the selected double reward campaign option',
  })

  const learnMoreLabel = intl.formatMessage({
    id: '-KZ_J4H7T1JxPF7QCGzU',
    defaultMessage: 'Learn more',
    description: 'Link to the double rewards campaign page in the reward selection modal footer',
  })

  const termsLabel = intl.formatMessage({
    id: 'kJTM7EndVjOlZuxICEf2',
    defaultMessage: 'Terms and conditions',
    description: 'Link to promotion terms below the choices in the double rewards selection modal',
  })

  const pointsDetails = intl.formatMessage({
    id: 'A0rXsRec_rp7L8jZWDAL',
    defaultMessage: 'How you can use your points',
    description: 'Expandable explanation of points in the double rewards selection modal',
  })

  const statusDetails = intl.formatMessage({
    id: 'se91pAI1YkFv3X9sM0HO',
    defaultMessage: 'How status credits help',
    description: 'Expandable explanation of status credits in the double rewards selection modal',
  })

  const bothOptionsIntro = endsAt ? intl.formatMessage({
    id: 'Qg2rxRYVnmo0LCUXCJoX',
    defaultMessage: 'Select Double {loyaltyProgramName} Points or Double Status Credits. Your choice will apply to every eligible booking you make before {date}. You can’t change it after you confirm.',
    description: 'Explanation of reward selection and campaign deadline in the double rewards selection modal',
  }, {
    loyaltyProgramName,
    date: <FormatDateTime value={endsAt} day="numeric" month="long" year="numeric" />,
  }) : undefined

  const pointsOnlyIntro = endsAt ? intl.formatMessage({
    id: 'DNgw5KE_DfxAqfBSTEKJ',
    defaultMessage: 'Select Double {loyaltyProgramName} Points for every eligible booking you make before {date}. You can’t change your reward after you confirm.',
    description: 'Points-only explanation for members unable to choose status credits in the reward selection modal',
  }, {
    loyaltyProgramName,
    date: <FormatDateTime value={endsAt} day="numeric" month="long" year="numeric" />,
  }) : undefined

  const pointsOptionLabel = intl.formatMessage({
    id: 'fDPwbzRgFedApvD5b6B4',
    defaultMessage: 'Double {loyaltyProgramName} Points',
    description: 'Points option label in the double rewards selection modal',
  }, { loyaltyProgramName })

  const pointsOptionDescription = intl.formatMessage({
    id: 'QTC6K8ld0181wUYXSazB',
    defaultMessage: 'Turn points into savings on your next escape.',
    description: 'Description of the points option in the double rewards selection modal',
  })

  const pointsBalance = account ? intl.formatMessage({
    id: 'RJuTViyrQirTNsG1w6j3',
    defaultMessage: 'Your current balance: <bold>{points, number} pts</bold>',
    description: 'Member points balance shown in the double rewards selection modal',
  }, { points: account.points, bold: renderBold }) : undefined

  const pointsSpendDetail = intl.formatMessage({
    id: 'AIIp83uOhHo6lmEwO8Du',
    defaultMessage: 'Use your points to pay for all or part of a future escape.',
    description: 'Expanded explanation of points in the double rewards selection modal',
  })

  const pointsRewardSeatsDetail = regionCode === 'AU' ? intl.formatMessage({
    id: 'zR8RwW9Qa4IRcokkYMzh',
    defaultMessage: 'Use your points to book <link>{loyaltyProgramName} Reward Seats</link> on our exclusive Maldives flights.',
    description: 'Reward seats benefit in the expanded points choice in the double rewards selection modal',
  }, { loyaltyProgramName, link: renderRewardSeatsLink }) : intl.formatMessage({
    id: 'g7TyP-3Fn4luOYAgVC-9',
    defaultMessage: 'Use your points across hotels, tours, cruises and experiences.',
    description: 'Points redemption benefit for international markets in the double rewards selection modal',
  })

  const statusOptionLabel = intl.formatMessage({
    id: 'cNbuYWJSon8F47z0lgmh',
    defaultMessage: 'Double Status Credits',
    description: 'Status credits option label in the double rewards selection modal',
  })

  const statusOptionDescription = tier === 'platinum' ? intl.formatMessage({
    id: 'PNV9PZ6zKKwh0FCnS8qt',
    defaultMessage: 'Earn double Status Credits on eligible bookings.',
    description: 'Eligible booking benefit for Platinum members in the double rewards selection modal',
  }) : intl.formatMessage({
    id: 'js3dd4tHeuh8hCSgQzq2',
    defaultMessage: 'Move closer to the next tier and more VIP perks.',
    description: 'Description of status credits in the double rewards selection modal',
  })

  const statusProgress = progress.hasNextTier && progress.membershipYearEndDate && progress.nextTierName ?
      <LoyaltyRewardChoiceTierProgress credits={progress.creditsNeeded} nights={progress.nightsNeeded} date={progress.membershipYearEndDate} tier={progress.nextTierName} /> :
    undefined

  const statusTierDetail = intl.formatMessage({
    id: 'F0WoZPL_Tu_bFTSlswpB',
    defaultMessage: 'Move closer to your next tier and its VIP benefits.',
    description: 'Expanded explanation of status credits in the double rewards selection modal',
  })

  const statusBenefitsDetail = tier === 'platinum' ? intl.formatMessage({
    id: 'wETfv0eVYHyKG_1D_7S4',
    defaultMessage: 'Enjoy the benefits of your current tier for longer.',
    description: 'Platinum tier benefit in the expanded status credits choice in the double rewards selection modal',
  }) : intl.formatMessage({
    id: 'AScld_qyRrCQpIj6eLN_',
    defaultMessage: 'Enjoy the benefits of your current tier for longer or unlock new ones sooner.',
    description: 'Tier benefits in the expanded status credits choice in the double rewards selection modal',
  })

  const primaryActionProps = useMemo(() => ({
    children: continueLabel,
    disabled: !availableSelection,
    onClick: continueSelection,
  }), [availableSelection, continueLabel, continueSelection])
  const secondaryActionProps = useMemo(() => ({
    children: learnMoreLabel,
    to: `/societe/${CHOOSE_DOUBLE_REWARDS_SLUG}`,
    target: '_blank',
    onClick: noop,
  }), [learnMoreLabel])

  return <>
    <LoyaltyRewardChoiceGradientHeader
      tier={tier}
      title={title}
      onClose={dismiss}
    />
    <ModalBody>
      <div>
        <ModalContent flushVerticalTop>
          <SelectionContent direction="vertical" gap={16}>
            <BodyText ref={impressionRef} variant="medium">
              {showStatusCredits ? bothOptionsIntro : pointsOnlyIntro}
            </BodyText>
            <Group direction="vertical" gap={12}>
              <Card>
                <Group direction="horizontal" gap={12}>
                  <RadioButton
                    id="double-reward-points"
                    name="double-reward"
                    checked={selection === 'points'}
                    onChange={selectPointsOption}
                  />
                  <OptionContent direction="vertical" gap={12}>
                    <Group direction="vertical" gap={8}>
                      <div>
                        <label htmlFor="double-reward-points">
                          <BodyText variant="medium" weight="bold">{pointsOptionLabel}</BodyText>
                        </label>
                        <BodyText variant="medium">{pointsOptionDescription}</BodyText>
                      </div>
                      {pointsBalance && <Sheet background={`tier-${tier}-light`} borderRadius="M" padding="8 12">
                        <BodyText variant="small">{pointsBalance}</BodyText>
                      </Sheet>}
                    </Group>
                    <Group direction="vertical">
                      <TextLink
                        size="small"
                        weight="regular"
                        onClick={togglePoints}
                        aria-expanded={pointsExpanded}
                        endIcon={pointsExpanded ? <LineAngleUpIcon size="S" /> : <LineAngleDownIcon size="S" />}
                      >
                        {pointsDetails}
                      </TextLink>
                      <Expander expanded={pointsExpanded}>
                        <div aria-hidden={!pointsExpanded}>
                          <DetailList>
                            <li><BodyText as="span" variant="small">{pointsSpendDetail}</BodyText></li>
                            <li><BodyText as="span" variant="small">{pointsRewardSeatsDetail}</BodyText></li>
                          </DetailList>
                        </div>
                      </Expander>
                    </Group>
                  </OptionContent>
                </Group>
              </Card>
              {showStatusCredits && <Card>
                <Group direction="horizontal" gap={12}>
                  <RadioButton
                    id="double-reward-status"
                    name="double-reward"
                    checked={selection === 'statusCredits'}
                    onChange={selectStatusCreditsOption}
                  />
                  <OptionContent direction="vertical" gap={12}>
                    <Group direction="vertical" gap={8}>
                      <div>
                        <label htmlFor="double-reward-status">
                          <BodyText variant="medium" weight="bold">{statusOptionLabel}</BodyText>
                        </label>
                        <BodyText variant="medium">{statusOptionDescription}</BodyText>
                      </div>
                      {(platinumRetention || statusProgress) && <Sheet background={`tier-${tier}-light`} borderRadius="M" padding="8 12">
                        <BodyText variant="small">{platinumRetention ? <LoyaltyPlatinumRetentionMessage {...platinumRetention} /> : statusProgress}</BodyText>
                      </Sheet>}
                    </Group>
                    <Group direction="vertical">
                      <TextLink
                        size="small"
                        weight="regular"
                        onClick={toggleStatus}
                        aria-expanded={statusExpanded}
                        endIcon={statusExpanded ? <LineAngleUpIcon size="S" /> : <LineAngleDownIcon size="S" />}
                      >
                        {statusDetails}
                      </TextLink>
                      <Expander expanded={statusExpanded}>
                        <div aria-hidden={!statusExpanded}>
                          <DetailList>
                            {tier !== 'platinum' && <li><BodyText as="span" variant="small">{statusTierDetail}</BodyText></li>}
                            <li><BodyText as="span" variant="small">{statusBenefitsDetail}</BodyText></li>
                          </DetailList>
                        </div>
                      </Expander>
                    </Group>
                  </OptionContent>
                </Group>
              </Card>}
            </Group>
            <BodyText variant="small">
              <TextLink to="/promotions" target="_blank" size="small" weight="regular" underline>{termsLabel}</TextLink>
            </BodyText>
          </SelectionContent>
        </ModalContent>
      </div>
    </ModalBody>
    <ModalFooter primaryActionProps={primaryActionProps} secondaryActionProps={secondaryActionProps} />
  </>
}

export default LoyaltySelectRewardChoiceContent
